import Link from '@/components/Link';
import { GuideHtmlPageFragment } from '@/graphql';
import HTMLToReact, { Parser as HTMLParser } from 'html-to-react';
import { snakeCase } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import reactToText from 'react-to-text';
import Styled from './HTMLPage.styled';

const htmlToReactParser = new HTMLParser();
const processNodeDefinitions = new HTMLToReact.ProcessNodeDefinitions();

type TableOfContents = {
  id: string;
  level: number;
  text: string;
}[];

interface GuideHTMLPageProps {
  entry: GuideHtmlPageFragment;
}

const GuideHTMLPage: PageTemplate<GuideHTMLPageProps> = ({ entry }) => {
  const [chapters, setChapters] = useState<TableOfContents>([]);

  const parseHtml = useCallback((html: string | null | undefined) => {
    let headings: TableOfContents = [];

    const parsedHtml = htmlToReactParser.parseWithInstructions(html ?? '', () => true, [
      {
        shouldProcessNode: (node) => /^h[0-2]$/i.test(node.tagName),
        processNode: (node, children, index) => {
          const text = reactToText(children).replaceAll(/\s+/g, ' ');
          const id = snakeCase(text);
          const level = parseInt((node.name as string).replace('h', ''), 10);

          if (level > 1) {
            headings.push({
              id,
              text,
              level,
            });
          }

          return <span id={id}>{processNodeDefinitions.processDefaultNode(node, children, index)}</span>;
        },
      },
      {
        shouldProcessNode: (node) => true,
        processNode: processNodeDefinitions.processDefaultNode,
      },
    ]);

    setChapters(headings);

    return parsedHtml;
  }, []);

  const html = useMemo(() => parseHtml(entry?.guidePageContent), [entry]);

  return (
    <>
      <Styled.Header>
        <Styled.HeaderImage
          src="/_next/image?url=https%3A%2F%2Ffront.com%2Fassets%2Fshared-inbox.png&amp;w=1920&amp;q=75"
          width={618}
          height={400}
          alt="image"
        />
      </Styled.Header>
      <Styled.MainContent>
        {chapters.length > 0 && (
          <Styled.ChapterListColumn>
            <Styled.ChapterListWrapper>
              <Styled.ChapterListHeading>Table of Contents</Styled.ChapterListHeading>

              <Styled.ChapterList>
                {chapters.map((chapter, index) => (
                  <Styled.ChapterListItem key={chapter.id} level={chapter.level}>
                    <Link href={`#${chapter.id}`} shallow>
                      <a>
                        <span>{chapter.text.replace(/[:\s]+?$/gi, '')}</span>
                      </a>
                    </Link>
                  </Styled.ChapterListItem>
                ))}
              </Styled.ChapterList>
            </Styled.ChapterListWrapper>
          </Styled.ChapterListColumn>
        )}

        <Styled.Content fullWidth={chapters.length === 0}>
          <Styled.HTMLContainer><h1>{entry.title}</h1></Styled.HTMLContainer>
          <Styled.HTMLContainer>{html}</Styled.HTMLContainer>
        </Styled.Content>
      </Styled.MainContent>
    </>
  );
};

export default GuideHTMLPage;
